/**
 * Seconds to wait, read from a `Retry-After` response header.
 *
 * RFC 9110 allows two forms and servers in this stack send both over time: a
 * delta-seconds count, and an HTTP-date. Returns undefined when the header is
 * absent or unparseable, so a caller falls back to its own backoff rather than
 * treating a malformed header as "retry immediately".
 */
export function parseRetryAfterSeconds(
  header: string | null | undefined,
  now: () => number = Date.now,
): number | undefined {
  if (header == null) return undefined
  const raw = header.trim()
  if (raw === '') return undefined

  // delta-seconds. Tested before the date form because `Date.parse` accepts a
  // bare integer on some engines and would read "120" as a year.
  if (/^\d+$/.test(raw)) return clamp(Number(raw))

  const at = Date.parse(raw)
  if (Number.isNaN(at)) return undefined
  return clamp((at - now()) / 1000)
}

/**
 * A window that has already passed means "retry now", not "wait a negative
 * time". The upper bound is a guard, not a protocol rule: the limiter this
 * talks to uses a one-minute window, so anything beyond a few minutes is a
 * broken or hostile header, and honouring it would hang a map save for as long
 * as it asked.
 */
const MAX_RETRY_AFTER_SECONDS = 300

function clamp(seconds: number): number {
  if (!Number.isFinite(seconds)) return 0
  return Math.min(Math.max(seconds, 0), MAX_RETRY_AFTER_SECONDS)
}
